import {
    DesignClass,
    NodeTypeName,
    ObjectClass
} from "@app/types";

/**
 * JSDoc imports.
 *
 * @import {
 *   Design,
 *   DesignObject
 * } from '@app/types';
 */

export default class ObjectsUsedIn {
    /**
     * Get a list of @see {DesignObject}s with duplicate names.
     *
     * @param {DesignObject[]} objects
     * @param {Design} serialized
     * @returns {Record<string, DesignObject[]>}
     */
    static getDuplicateObjectNamesMap(objects, serialized){
        const isCommonModuleApp = serialized.client === 'InteractionsCommon';
        const nonCommonModuleObjects = objects.filter(obj => !obj.props.moduleApp);
        
        const useObjects = (isCommonModuleApp) ? serialized.components.objects : nonCommonModuleObjects;
        
        const duplicateObjectsNamesMap = {};
        const distinctNodeTypes = (type, idx, self) => { 
            return self.indexOf(type) === idx;
        };
        const objNodeTypes = useObjects.map(obj => obj.props.nodeTypeName);
        const uniqueTypes = objNodeTypes.filter(distinctNodeTypes);
        
        uniqueTypes.forEach(type => {
            if(type !== NodeTypeName.begin){
                const objTypes = useObjects.filter(obj => obj.props.nodeTypeName === type);
                const typeNamesMap = {};
                objTypes.forEach(obj => {
                    typeNamesMap[obj.name] = typeNamesMap[obj.name] || [];
                    typeNamesMap[obj.name].push(obj);
                });
                
                Object.values(typeNamesMap).forEach(nm => {
                    if(nm.length > 1){
                        duplicateObjectsNamesMap[type] = duplicateObjectsNamesMap[type] || [];
                        duplicateObjectsNamesMap[type].push(nm)
                    }
                });
                
            }
            
            
        });
        
        return duplicateObjectsNamesMap
    
    }

    /**
     * @param {Design} serialized
     * @returns {DesignObject[]}
     */
    static getUnusedObjects(serialized){
        const unusedObjects = [];
        const isCommonModuleApp = serialized.client === 'InteractionsCommon';
        const nonCommonModuleObjects = serialized.components.objects.filter(obj => !obj.props.moduleApp);
        const objects = (isCommonModuleApp) ? serialized.components.objects : nonCommonModuleObjects;
        
        objects.filter(obj => obj._class !== ObjectClass.begin).forEach(obj => {
            const objectsUsedIn = ObjectsUsedIn.getObjectUsedIn(obj.id, serialized);
            if(objectsUsedIn.filter(y => y.length > 0).length === 0){
                unusedObjects.push(obj);
            } 
        });
        
        return unusedObjects;
    }
    
    /**
     * Precomputed projection of @see serialized for @see getObjectUsedIn.
     * Pure; safe to discard after a batch of calls returns.
     *
     * @param {Design} serialized
     */
    static buildSharedIndex(serialized){
        const promptIdRefs = {};
        const playSavedPromptIdRefs = {};
        const dataNodeInstanceMap = {};
        const dataAccessNodeRefs = {};
        const promptIsReferenceableRefs = {};
        const moduleById = new Map();
        const objectById = new Map();
        const withFillerByReferenceId = new Map();
        const flowsByParentComponentID = new Map();
        const localVarsByPromptName = new Map();
        const moduleInputsByPromptName = new Map();

        const pushInto = (map, key, entry) => {
            const bucket = map.get(key);
            if(bucket){
                bucket.push(entry);
            } else {
                map.set(key, [entry]);
            }
        };

        serialized.components.modules.forEach(mod => {
            moduleById.set(mod.id, mod);
        });

        // Second pass: needs `moduleById` populated to resolve
        // parentComponentID for the moduleInputs precompute.
        serialized.components.modules.forEach(mod => {
            const modLocalVars = mod.localVariables || [];
            modLocalVars.forEach(lv => {
                const cfg = lv.props.config;
                if(cfg.type === "PROMPT" && cfg.defaultValue){
                    pushInto(localVarsByPromptName, cfg.defaultValue, { mod, variable: lv });
                }
            });

            const { nodes } = mod.versions[1].flow;
            nodes.forEach(n => {
                const pcid = n.props.parentComponentID;
                if(pcid){
                    pushInto(flowsByParentComponentID, pcid, { mod, n });
                }

                if(n._class !== DesignClass.module) return;
                const inputs = n.props.config && n.props.config.inputs;
                if(!inputs || inputs.length === 0) return;
                const parentModule = moduleById.get(pcid);
                if(!parentModule) return;
                const parentLocalVars = parentModule.localVariables;
                if(!parentLocalVars || parentLocalVars.length === 0) return;

                inputs.forEach(input => {
                    if(input.type !== "value" || !input.value) return;
                    // Preserve original semantics: one entry per matching localVariable.
                    parentLocalVars.forEach(lv => {
                        if(input.argumentName.indexOf("mod." + lv.name) !== -1){
                            pushInto(moduleInputsByPromptName, input.value, { mod, n });
                        }
                    });
                });
            });
        });

        serialized.components.objects.forEach(obj => {
            objectById.set(obj.id, obj);

            // capture prompts referenced in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
            if(obj._class === ObjectClass.savePrompt || obj._class === ObjectClass.playSavedPrompt){
                promptIdRefs[obj.props.config.promptId] = promptIdRefs[obj.props.config.promptId] || [];
                promptIdRefs[obj.props.config.promptId].push(obj);
            }

            // capture playSavedPrompts referenced in config.playSavedPromptId of SavePromptNodeModel nodes
            if(obj._class === ObjectClass.savePrompt){
                playSavedPromptIdRefs[obj.props.config.playSavedPromptId] = playSavedPromptIdRefs[obj.props.config.playSavedPromptId] || [];
                playSavedPromptIdRefs[obj.props.config.playSavedPromptId].push(obj);
            }

            if(obj._class === ObjectClass.data){
                dataNodeInstanceMap[obj.props.parentComponentID] = dataNodeInstanceMap[obj.props.parentComponentID] || [];
                dataNodeInstanceMap[obj.props.parentComponentID].push(obj);
            }

            if(obj._class === ObjectClass.dataAccessCompletion){
                dataAccessNodeRefs[obj.props.config.dataAccessNode] = dataAccessNodeRefs[obj.props.config.dataAccessNode] || [];
                dataAccessNodeRefs[obj.props.config.dataAccessNode].push(obj);
            }

            if(obj._class === ObjectClass.prompt){
                if(obj.props.config.isReferenceable){
                    promptIsReferenceableRefs[obj.id] = promptIsReferenceableRefs[obj.id] || [];
                    promptIsReferenceableRefs[obj.id].push(obj);
                }
            }

            if(obj._class === ObjectClass.withFiller){
                const refId = obj.props.config.referenceId;
                if(refId){
                    pushInto(withFillerByReferenceId, refId, obj);
                }
            }
        });

        return {
            dataAccessNodeRefs,
            dataNodeInstanceMap,
            flowsByParentComponentID,
            localVarsByPromptName,
            moduleById,
            moduleInputsByPromptName,
            objectById,
            playSavedPromptIdRefs,
            promptIdRefs,
            promptIsReferenceableRefs,
            withFillerByReferenceId
        };
    }

    /**
     * Get all uses of the given @see objId within @see serialized.
     * @param {string} objId The ID of the object to find uses for.
     * @param {Design} serialized The serialized data of the app.
     * @param {{ excludeDisabledInFlow: boolean; }} [config]
     * @param {ReturnType<typeof ObjectsUsedIn.buildSharedIndex>} [sharedIndex] Precomputed index; built lazily if omitted.
     */
    static getObjectUsedIn(objId, serialized, config, sharedIndex) {
        sharedIndex ??= ObjectsUsedIn.buildSharedIndex(serialized);

        const { excludeDisabledInFlow = false } = config ?? {};
        const {
            dataAccessNodeRefs,
            dataNodeInstanceMap,
            flowsByParentComponentID,
            localVarsByPromptName,
            moduleInputsByPromptName,
            objectById,
            playSavedPromptIdRefs,
            promptIdRefs,
            promptIsReferenceableRefs,
            withFillerByReferenceId
        } = sharedIndex;
        const usedInObject = objectById.get(objId);
        // data structures
        const usedIn = [];
        const usedInFlows = [];
        const usedInModuleInputs = [];
        const usedInLocalVariables = [];
        const usedInObjects = [];
        const usedInGlobalIntents = [];

        const isPromptWithName = usedInObject._class === ObjectClass.prompt
            && usedInObject.name !== "";

        if(isPromptWithName){
            const lvBucket = localVarsByPromptName.get(usedInObject.name);
            if(lvBucket){
                lvBucket.forEach(entry => usedInLocalVariables.push(entry));
            }

            const miBucket = moduleInputsByPromptName.get(usedInObject.name);
            if(miBucket){
                miBucket.forEach(({ mod, n }) => {
                    usedInModuleInputs.push({ mod, nod: n });
                });
            }
        }

        const flowBucket = flowsByParentComponentID.get(objId);
        if(flowBucket){
            flowBucket.forEach(({ mod, n }) => {
                // DEVES2-1118: Mark prompts as unused if they are used in
                // disabled modules.
                // DEVDES2-965: exclude disabledInFlow prompt from used in.
                if(excludeDisabledInFlow){
                    if(!mod.props.config.disabledInFlow && !n.props.config.disabledInFlow){
                        usedInFlows.push(mod.id);
                    }
                } else {
                    usedInFlows.push(mod.id);
                }
            });
        }

        const withFillerRefs = withFillerByReferenceId.get(objId);
        if(withFillerRefs){
            withFillerRefs.forEach(obj => usedInObjects.push(obj.id));
        }

        if(promptIsReferenceableRefs[objId]){
            usedInObjects.push(objId)
        }

        // prompts used in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
        if(promptIdRefs[objId]){
            promptIdRefs[objId].forEach(obj => usedInObjects.push(obj.id));
        }
        if (usedInObject._class === ObjectClass.prompt&&usedInObject.name!=="") {
            serialized.globalIntents.forEach(gi => {
                if(gi.moduleProperties){
                    if (gi.moduleProperties.props.config.inputs && gi.moduleProperties.props.config.inputs.length > 0) {
                        if (gi.moduleProperties.props.config.inputs.findIndex(input => input.value === usedInObject.name) !== -1) {
                            usedInGlobalIntents.push({
                                gi,
                                type: 'inGlobalIntent'
                            });
                        }
                    }
                }
            });
        } else if (
            usedInObject._class === ObjectClass.custom
            && usedInObject.id === serialized.globalErrorHandling?.postProcessingCustom
        ) {
            usedIn.push({
                name: 'Post Processing Custom',
                subtitle: 'Global Error Handling',
                title: 'Settings'
            });
        }

        // prompts used in config.promptId of SavePromptNodeModel and PlaySavedPromptNodeModel nodes
        // playSavedPrompts used in config.playSavedPromptId of SavePromptNodeModel
        if(playSavedPromptIdRefs[objId]){
            playSavedPromptIdRefs[objId].forEach(obj => usedInObjects.push(obj.id));
        }

        // data node objects rerefenced in config.dataAccessNode of DataAccessCompletionNodeModel
        if(dataNodeInstanceMap[objId]){
            dataNodeInstanceMap[objId].forEach(inst => {
                if (dataAccessNodeRefs[inst.id]) {
                    dataAccessNodeRefs[inst.id].forEach(ref => usedInObjects.push(ref.props.parentComponentID));
                }
            });
        }
              
           // if this order is changed make sure to check all the places we are using Usedin  
        return [
            usedInFlows,
            usedInObjects,
            usedInGlobalIntents,
            usedInLocalVariables,
            usedInModuleInputs,
            usedIn
        ];
    }
}